import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const TimelineBudgetStep = ({ formData, updateFormData, errors }) => {
  const urgencyLevels = [
    {
      value: 'low',
      label: 'Geen haast',
      description: 'Ik oriënteer me nog',
      icon: 'Clock',
      color: 'text-muted-foreground'
    },
    {
      value: 'medium',
      label: 'Binnen 3 maanden',
      description: 'Ik wil graag starten',
      icon: 'Calendar',
      color: 'text-info'
    },
    {
      value: 'high',
      label: 'Binnen 1 maand',
      description: 'Ik wil snel starten',
      icon: 'Zap',
      color: 'text-warning'
    },
    {
      value: 'urgent',
      label: 'Zo snel mogelijk',
      description: 'Het is urgent',
      icon: 'AlertCircle',
      color: 'text-error'
    }
  ];

  const budgetRanges = [
    { value: '', label: 'Selecteer budgetbereik' },
    { value: '< €5.000', label: 'Minder dan €5.000' },
    { value: '€5.000 - €10.000', label: '€5.000 - €10.000' },
    { value: '€10.000 - €15.000', label: '€10.000 - €15.000' },
    { value: '€15.000 - €25.000', label: '€15.000 - €25.000' },
    { value: '€25.000 - €50.000', label: '€25.000 - €50.000' },
    { value: '> €50.000', label: 'Meer dan €50.000' },
    { value: 'Weet ik niet', label: 'Weet ik nog niet' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Planning & Budget</h2>
        <p className="text-muted-foreground">Wanneer wilt u starten en wat is uw budget?</p>
      </div>

      {/* Urgency Selection */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Wanneer wilt u starten? <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {urgencyLevels?.map(level => (
            <button
              key={level?.value}
              type="button"
              onClick={() => updateFormData({ urgency: level?.value })}
              className={cn(
                "flex items-start p-4 rounded-lg border-2 transition-all text-left hover:shadow-md",
                formData?.urgency === level?.value
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              <Icon 
                name={level?.icon} 
                size={24} 
                className={cn(
                  "flex-shrink-0 mt-0.5",
                  formData?.urgency === level?.value ? "text-primary" : level?.color
                )} 
              />
              <div className="ml-3 flex-1">
                <p className={cn(
                  "text-sm font-semibold",
                  formData?.urgency === level?.value ? "text-primary" : "text-foreground"
                )}>
                  {level?.label}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{level?.description}</p>
              </div>
              {formData?.urgency === level?.value && (
                <Icon name="CheckCircle" size={20} className="text-primary flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
        {errors?.urgency && (
          <p className="text-sm text-destructive mt-2">{errors?.urgency}</p>
        )}
      </div>

      {/* Preferred Start Date */}
      <Input
        label="Gewenste startdatum (optioneel)"
        type="date"
        value={formData?.preferredStartDate}
        onChange={(e) => updateFormData({ preferredStartDate: e?.target?.value })}
        description="Geef een indicatie wanneer u zou willen starten"
        min={new Date()?.toISOString()?.split('T')?.[0]}
      />

      {/* Budget Range */}
      <Select
        label="Indicatief budget"
        required
        options={budgetRanges}
        value={formData?.budgetRange}
        onChange={(value) => updateFormData({ budgetRange: value })}
        error={errors?.budgetRange}
        placeholder="Selecteer uw budgetbereik"
        description="Dit helpt providers passende oplossingen voor te stellen"
      />

      {/* Financing Needed */}
      <div className="bg-accent/50 border border-border rounded-lg p-4">
        <label className="flex items-start cursor-pointer">
          <Checkbox
            checked={formData?.financingNeeded}
            onCheckedChange={(checked) => updateFormData({ financingNeeded: checked })}
            className="mt-1"
          />
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-foreground">Ik ben geïnteresseerd in financieringsmogelijkheden</p>
            <p className="text-xs text-muted-foreground mt-1">
              Veel providers bieden financieringsopties en kunnen u helpen met subsidieaanvragen
            </p>
          </div>
        </label>
      </div>

      {/* Subsidy Information */}
      <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-start space-x-3">
        <Icon name="Info" size={20} className="text-success flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-foreground font-medium mb-1">Subsidies beschikbaar</p>
          <p className="text-xs text-muted-foreground mb-2">
            Voor veel verduurzamingsprojecten zijn subsidies beschikbaar. Providers kunnen u helpen met:
          </p>
          <ul className="text-xs text-muted-foreground space-y-1 ml-4">
            <li>• ISDE subsidie (warmtepompen, zonneboilers)</li>
            <li>• Subsidie Energiebesparing Eigen Huis (SEEH)</li>
            <li>• BTW-verlaging naar 0% voor verduurzaming</li>
            <li>• Gemeentelijke regelingen</li>
          </ul>
        </div>
      </div>

      {/* Estimated Timeline Info */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="Clock" size={18} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Geschatte doorlooptijd</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Offerte ontvangen</p>
            <p className="text-lg font-bold text-primary">1-3 dagen</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Intake & advies</p>
            <p className="text-lg font-bold text-primary">1-2 weken</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Start werkzaamheden</p>
            <p className="text-lg font-bold text-primary">2-6 weken</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-2">
          * Afhankelijk van project complexiteit en beschikbaarheid
        </p>
      </div>
    </div>
  );
};

export default TimelineBudgetStep;